import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { smsApi } from "../lib/api";
import {
  MessageSquareText,
  Send,
  Loader2,
  Save,
  Phone,
  Star,
  Receipt,
  Search,
  CheckCircle2,
  PauseCircle,
} from "lucide-react";

const inputClass =
  "w-full h-9 px-2.5 rounded-md border border-slate-200 bg-white text-[13px] text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-colors";

/** Messaging integrations card — text number + Google review link + PDF receipt toggle.
 *  Automations were moved to the dedicated Automations page. */
export default function MessagingIntegrationsCard() {
  const [settings, setSettings] = useState(null);
  const [testPhone, setTestPhone] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [dirty, setDirty] = useState(false);

  const reload = async () => {
    try {
      const s = await smsApi.getSettings();
      setSettings(s);
      setDirty(false);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load messaging settings");
    }
  };
  useEffect(() => {
    reload();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        reviewLink: settings.reviewLink || "",
        emailReceiptOnPaid: !!settings.emailReceiptOnPaid,
      };
      const updated = await smsApi.updateSettings(payload);
      setSettings((s) => ({ ...updated, managed: s.managed, managedAvailable: s.managedAvailable }));
      setDirty(false);
      toast.success("Messaging settings saved");
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    if (!testPhone.trim()) {
      toast.error("Enter a phone number to test (E.164, e.g. +14155552671)");
      return;
    }
    setTesting(true);
    try {
      const res = await smsApi.sendTest(testPhone.trim());
      toast.success(`Test SMS sent (${res.status || "queued"})`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Test SMS failed");
    } finally {
      setTesting(false);
    }
  };

  if (!settings) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="messaging-card">
        <div className="text-center py-6 text-slate-400">
          <Loader2 className="h-4 w-4 inline animate-spin" />
        </div>
      </div>
    );
  }

  const managed = settings.managed || {};
  const legacyConnected =
    settings.twilio?.accountSid && settings.twilio?.authTokenSet && settings.twilio?.fromNumber;
  const connected = !!managed.number || legacyConnected;

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="messaging-card">
      <div className="flex items-center gap-2 mb-1">
        <MessageSquareText className="h-4 w-4 text-blue-600" />
        <h2 className="text-[14px] font-semibold text-slate-900">Text messaging</h2>
        <span
          data-testid="sms-connection-status"
          className={`ml-1 text-[10.5px] font-semibold px-1.5 py-0.5 rounded border ${
            connected
              ? "text-emerald-700 bg-emerald-50 border-emerald-200"
              : "text-amber-700 bg-amber-50 border-amber-200"
          }`}
        >
          {connected ? "Texting connected" : "No text number yet"}
        </span>
      </div>
      <p className="text-[12px] text-slate-500 mb-4">
        Claim a dedicated text number for your company. Message templates are now managed as automations on
        the <span className="font-semibold text-slate-700">Automations</span> page.
      </p>

      <ManagedNumberPanel
        managed={managed}
        available={!!settings.managedAvailable}
        legacyConnected={legacyConnected}
        onClaimed={reload}
      />

      {/* Review link + Test SMS */}
      <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-4 mb-4">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Google review link">
            <div className="relative">
              <Star className="h-3.5 w-3.5 text-amber-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                value={settings.reviewLink || ""}
                onChange={(e) => {
                  setSettings((s) => ({ ...s, reviewLink: e.target.value }));
                  setDirty(true);
                }}
                data-testid="sms-review-link"
                placeholder="https://g.page/r/.../review"
                className={`${inputClass} pl-8`}
              />
            </div>
          </Field>
          <Field label="Send a test text">
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Phone className="h-3.5 w-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  value={testPhone}
                  onChange={(e) => setTestPhone(e.target.value)}
                  data-testid="sms-test-phone"
                  placeholder="+14155552671"
                  className={`${inputClass} pl-8`}
                />
              </div>
              <Button
                onClick={sendTest}
                disabled={testing}
                data-testid="sms-test-btn"
                className="h-9 bg-[#0B1E3F] hover:bg-[#0B1E3F]/90 text-white text-[13px] font-semibold"
              >
                {testing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />}
                Send test
              </Button>
            </div>
          </Field>
        </div>
      </div>

      {/* Email receipt toggle */}
      <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white p-3.5">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 text-[13px] font-semibold text-slate-900">
            <Receipt className="h-3.5 w-3.5 text-blue-600" /> Email a PDF receipt when an estimate or invoice is paid
          </div>
          <div className="text-[11.5px] text-slate-500 mt-0.5">
            Sends the customer a PDF receipt automatically — the moment an invoice's balance reaches $0, or
            the moment a deposit/payment is collected on an estimate.
          </div>
        </div>
        <button
          onClick={() => {
            setSettings((s) => ({ ...s, emailReceiptOnPaid: !s.emailReceiptOnPaid }));
            setDirty(true);
          }}
          data-testid="sms-email-receipt-toggle"
          role="switch"
          aria-checked={!!settings.emailReceiptOnPaid}
          className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
            settings.emailReceiptOnPaid ? "bg-emerald-500" : "bg-slate-300"
          }`}
        >
          <span
            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
              settings.emailReceiptOnPaid ? "translate-x-5" : "translate-x-0.5"
            }`}
          />
        </button>
      </div>

      <div className="flex justify-end mt-4">
        <Button
          onClick={save}
          disabled={!dirty || saving}
          data-testid="sms-save-btn"
          className="h-10 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-200 disabled:text-slate-500 text-white text-[13px] font-semibold"
        >
          {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
          Save messaging settings
        </Button>
      </div>
    </div>
  );
}

/** Managed text number: shows the claimed number, or a search-and-claim flow. */
function ManagedNumberPanel({ managed, available, legacyConnected, onClaimed }) {
  const [areaCode, setAreaCode] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);
  const [claiming, setClaiming] = useState("");

  const search = async () => {
    setSearching(true);
    setResults(null);
    try {
      const res = await smsApi.searchNumbers(areaCode.trim());
      setResults(res.numbers || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Number search failed");
    } finally {
      setSearching(false);
    }
  };

  const claim = async (phoneNumber) => {
    setClaiming(phoneNumber);
    try {
      await smsApi.claimNumber(phoneNumber);
      toast.success(`${phoneNumber} is now your company text number!`);
      onClaimed();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not claim this number");
    } finally {
      setClaiming("");
    }
  };

  if (managed.number) {
    const suspended = managed.status && managed.status !== "active";
    return (
      <div
        data-testid="sms-managed-number"
        className={`rounded-lg border p-4 mb-4 ${suspended ? "border-amber-200 bg-amber-50/50" : "border-emerald-200 bg-emerald-50/50"}`}
      >
        <div className="flex items-center gap-2">
          {suspended ? <PauseCircle className="h-4 w-4 text-amber-600" /> : <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
          <span className="text-[13px] font-semibold text-slate-900">Your company text number:</span>
          <span className="text-[14px] font-bold text-slate-900 font-mono" data-testid="sms-managed-number-value">{managed.number}</span>
          <span className={`text-[10.5px] font-semibold px-1.5 py-0.5 rounded border ${suspended ? "text-amber-700 bg-amber-100 border-amber-200" : "text-emerald-700 bg-emerald-100 border-emerald-200"}`}>
            {suspended ? managed.status : "active"}
          </span>
        </div>
        <p className="text-[11.5px] text-slate-500 mt-1">
          {suspended
            ? "Texting is paused on this account — contact support to reactivate."
            : "Outbound texts and customer replies (Inbox) use this number."}
        </p>
      </div>
    );
  }

  if (!available) {
    if (legacyConnected) return null;
    return (
      <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-4 mb-4" data-testid="sms-managed-unavailable">
        <div className="text-[13px] font-semibold text-slate-900">Texting is coming to your account</div>
        <p className="text-[12px] text-slate-500 mt-0.5">
          Text numbers are managed by the platform and aren&apos;t enabled yet. Check back soon — once live, you&apos;ll
          claim a local number right here.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-blue-200 bg-blue-50/40 p-4 mb-4" data-testid="sms-claim-panel">
      <div className="text-[13px] font-semibold text-slate-900 mb-0.5">Claim your company text number</div>
      <p className="text-[12px] text-slate-500 mb-3">
        Search for a local number by area code — it becomes the number customers see for all your texts.
      </p>
      <div className="flex items-end gap-2">
        <div className="w-40">
          <label className="block text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1">Area code</label>
          <input
            value={areaCode}
            onChange={(e) => setAreaCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 3))}
            data-testid="sms-area-code"
            placeholder="e.g. 415"
            className={inputClass}
          />
        </div>
        <Button
          onClick={search}
          disabled={searching}
          data-testid="sms-search-numbers-btn"
          className="h-9 bg-blue-600 hover:bg-blue-700 text-white text-[13px] font-semibold"
        >
          {searching ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Search className="h-4 w-4 mr-1.5" />}
          Search numbers
        </Button>
      </div>
      {results && (
        <div className="mt-3 space-y-1.5" data-testid="sms-number-results">
          {results.length === 0 ? (
            <div className="text-[12.5px] text-slate-500">No numbers found for that area code — try another.</div>
          ) : (
            results.map((n) => (
              <div key={n.phoneNumber} className="flex items-center justify-between rounded-md border border-slate-200 bg-white px-3 py-2">
                <div>
                  <span className="text-[13px] font-semibold text-slate-900 font-mono">{n.friendlyName || n.phoneNumber}</span>
                  {(n.locality || n.region) && (
                    <span className="ml-2 text-[11.5px] text-slate-500">{[n.locality, n.region].filter(Boolean).join(", ")}</span>
                  )}
                </div>
                <Button
                  onClick={() => claim(n.phoneNumber)}
                  disabled={!!claiming}
                  data-testid={`sms-claim-${n.phoneNumber}`}
                  className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white text-[12.5px] font-semibold"
                >
                  {claiming === n.phoneNumber ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Claim"}
                </Button>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1">{label}</label>
      {children}
    </div>
  );
}
